import Head from 'next/head'
import { useEffect } from 'react'
import { Container } from 'components/ui'
import type { FC } from 'react'
import Layout, { Breadcrumb, BreadcrumbProps } from 'components/layout'
import { ProductListView } from 'modules/products/containers'
import { ProductService } from 'modules/products/services'
import { CategoryService } from 'modules/categories/services'
import { Product } from 'framework/products'
import { Category } from 'framework/categories'
import parser from 'fazendinha/product'
import Cookies from 'cookies'
import { useRouter } from 'next/router'
import s from './products.module.css'

interface ProdutosSlugProps {
  products: Product[]
  category: Category
  search: string
  lastPage: number
}

const ProdutosSlug: FC<ProdutosSlugProps> = ({ category, products, search, lastPage }) => {
  const crumbs: BreadcrumbProps['crumbs'] = [{ url: '/', label: 'Produtos' }]
  const router = useRouter()

  const pageTitle = search !== '' ? `Busca por: ${search}` : category?.name || 'Produtos'

  const SearchTitle = () => {
    if (search !== '') {
      return (
        <>
          Resultados da busca por: <strong>{search}</strong>
        </>
      )
    }
    return <></>
  }

  const CategoryTitle = () => {
    return <>{category.name || ''}</>
  }

  useEffect(() => {
    if (category && category.name) {
      crumbs.push({ label: category.name })
    }

    if (search !== '') {
      crumbs.push({ label: `Resultado: ${search}` })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout>
      <Head>{pageTitle}</Head>
      <Breadcrumb crumbs={crumbs} />
      <Container className={s.root}>
        {category?.slug === 'experiencias' ||
        category?.slug === 'assinaturas' ||
        category?.slug === 'ceu-de-montanhas' ||
        category?.slug === 'selecao-especial-fazendinha'
        ? (
          <div>
            <img src={`/assets/images/banners/banner-${category.slug}.jpg`} alt="Experiencias" />
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '12px' }}>
              <img src="/assets/images/produtos/banner-produtos.jpg" alt="Produtos" />
            </div>
            <h1 className={s.title}>
              <SearchTitle />
              <CategoryTitle />
            </h1>
          </>
        )}
        {category.slug === 'selecao-especial-fazendinha' && (
          <div className="mx-auto text-center md:w-2/3">
            <h2 className="block my-4 px-4 text-lg font-bold md:px-0 md:text-3xl">Seleção Especial Fazendinha</h2>
            Uma Série Exclusiva Fazendinha em Casa de produtos artesanais, do campo, de sabores autênticos e alta qualidade, cuidadosamente elaborados por produtores locais da agricultura familiar.
          </div>
        )}
        {category.slug === 'assinaturas' && (
          <div className="text-center">
            <h2 className="block my-4 px-4 text-lg font-bold md:px-0 md:text-3xl">Compras em formato de Assinatura</h2>
            Assine e receba produtos artesanais, locais, direto do produtor, na periodicidade desejada, poupando seu
            tempo e sem riscos de esquecimento.
            <ul className="table m-auto my-3.5 px-16 w-auto text-left list-disc">
              <li>Frete grátis</li>
              <li>Sem taxa de adesão</li>
              <li>Cancele quando quiser na área do cliente aqui pelo site</li>
            </ul>
            As assinaturas só são possíveis de serem pagas com cartão de crédito.
          </div>
        )}
        {category.slug === 'ceu-de-montanhas' && (
          <>
            <div className="text-center">
              <h2 className="block my-4 px-4 text-lg font-bold md:px-0 md:text-3xl">Céu de Montanhas</h2>
            </div>
            <div className="px-2">
              <p>
                Céu de Montanhas é o coração de um movimento cultural e de inovação que vem transformando o turismo
                sustentável em Brumadinho, município de beleza natural ímpar que fica na região metropolitana de Belo
                Horizonte - MG. Um Turismo Rural e de Base Comunitária, fruto de uma construção coletiva entre
                empreendimentos e as comunidades que proporcionam experiências e produtos artesanais que dialogam com a
                diversidade do território.
              </p>
              <p>
                A coletânea tem a curadoria assinada pelo designer Ronaldo Fraga, pela especialista em turismo de
                experiência Mirian Rocha (que assina as novidades incorporadas em 2023) e oferece produtos e serviços
                que encantam turistas e contribuem para o crescimento dos negócios locais. Portanto curta as vivências,
                a gastronomia, design têxtil, artesanato, cerâmica e música. São cores, texturas, sabores e sons alegres
                que encantam quem visita Brumadinho.
              </p>
            </div>
          </>
        )}
        <ProductListView
          lastPage={lastPage}
          baseUrl={router.asPath}
          products={products}
          isExp={category.slug === 'experiencias'}
        />
      </Container>
    </Layout>
  )
}

export const getServerSideProps = async (ctx: any) => {
  const { query, params } = ctx

  const cookies = new Cookies(ctx.req, ctx.res)
  const zipCode = cookies.get('zipcode')

  const { page, days, orderby, sort, search, tags, zipcode } = query
  const { slug } = params
  const searchParam = search || ''

  const productService = new ProductService()
  const categoryService = new CategoryService()
  let category = {}

  if (slug) {
    category = await categoryService.getCategoryById(slug).then(({ data }) => categoryService.parser([data.data])[0])
  }

  const productQuery = {
    categories: (category as any)?.slug || '',
    page: page || 1,
    days,
    tags,
    orderby,
    sort,
    search: searchParam,
    zipcode: zipcode || zipCode,
  }

  try {
    const productsResponse = await productService.query(productQuery)

    return {
      props: {
        products: productsResponse.data.data.map((p: any) => parser(p)),
        lastPage: productsResponse.data.meta.last_page,
        category,
        search: searchParam,
      },
    }
  } catch (e) {
    return {
      props: {
        products: [],
        lastPage: 0,
        category,
        search: searchParam,
      },
    }
  }
}

export default ProdutosSlug
